import {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { AvatarChip } from "components/designV2/avatarChip/avatarChip";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { InputField } from "components/designV2/inputfield/inputfield";
import { Modal } from "components/designV2/modal/modal";
import { SidePanel } from "components/designV2/sidePanel/sidePanel";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import {
  filterWorkers,
  getWorkerName,
  getWorkerRole,
  hasOtherTeamMembership,
  isWorkerInactive,
} from "./organizationSettings.logic";
import type { OrganizationPerson } from "./organizationSettings.logic";
import {
  DangerCopy,
  DialogActions,
  DialogCopy,
  DialogForm,
  ErrorBox,
  IconButton,
  LoadingText,
  MemberIdentity,
  MemberRow,
  PanelActionContent,
  PanelActionRow,
  PanelInputContent,
  PanelRows,
  PanelSection,
  PanelSectionTitle,
  PanelLoading,
  PanelSpinner,
} from "./organizationSettings.styled";
import type { OrganizationResult } from "./useOrganizationSettings.hook";

type SaveOperation = (name: string) => Promise<OrganizationResult<void>>;
type CreateDialogKind = "entity" | "unit" | "team";

interface CreateDialogCopyKeys {
  readonly info: string;
  readonly name: string;
  readonly placeholder: string;
  readonly title: string;
}

const CREATE_DIALOG_COPY_KEYS: Record<CreateDialogKind, CreateDialogCopyKeys> =
  {
    entity: {
      info: "OrganizationSettings.CreateEntityInfo",
      name: "OrganizationSettings.EntityName",
      placeholder: "OrganizationSettings.EntityPlaceholder",
      title: "OrganizationSettings.CreateEntity",
    },
    team: {
      info: "OrganizationSettings.CreateTeamUnder",
      name: "OrganizationSettings.TeamName",
      placeholder: "OrganizationSettings.TeamPlaceholder",
      title: "OrganizationSettings.CreateTeam",
    },
    unit: {
      info: "OrganizationSettings.CreateUnitInfo",
      name: "OrganizationSettings.UnitName",
      placeholder: "OrganizationSettings.UnitPlaceholder",
      title: "OrganizationSettings.CreateUnit",
    },
  };

interface CreateDialogProps {
  readonly isOpen: boolean;
  readonly kind: CreateDialogKind;
  readonly parentName?: string;
  readonly onClose: () => void;
  readonly onSave: SaveOperation;
}

export const OrganizationCreateDialog = (
  props: CreateDialogProps,
): JSX.Element => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!props.isOpen) return;
    setName("");
    setHasError(false);
  }, [props.isOpen]);

  const handleSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!name.trim()) return;
    setIsSaving(true);
    const result = await props.onSave(name.trim());
    setIsSaving(false);
    if (result.ok) {
      props.onClose();
      return;
    }
    setHasError(true);
  };

  const handleClose = (): void => {
    if (!isSaving) props.onClose();
  };

  const copyKeys = CREATE_DIALOG_COPY_KEYS[props.kind];
  const title = t(copyKeys.title);
  const info = t(
    copyKeys.info,
    props.kind === "team" ? { unit: props.parentName } : undefined,
  );

  return (
    <Modal
      label={title}
      size="compact"
      showCloseButton={false}
      isOpen={props.isOpen}
      onClose={handleClose}
    >
      <DialogForm onSubmit={handleSubmit}>
        <DialogCopy>{info}</DialogCopy>
        <InputField
          size="compact"
          label={t(copyKeys.name)}
          value={name}
          onChange={setName}
          maxLength={100}
          fullWidth
          hasError={hasError}
          errorText={t("OrganizationSettings.RequestFailed")}
          placeholder={t(copyKeys.placeholder)}
        />
        <DialogActions>
          <SecondaryButton
            size="small"
            label={t("OrganizationSettings.Cancel")}
            disabled={isSaving}
            onClick={handleClose}
          />
          <PrimaryButton
            size="small"
            type="submit"
            label={title}
            disabled={!name.trim()}
            isLoading={isSaving}
          />
        </DialogActions>
      </DialogForm>
    </Modal>
  );
};

export type OrganizationEditTarget =
  | {
      readonly id: number;
      readonly kind: "entity";
      readonly name: string;
    }
  | {
      readonly id: number;
      readonly kind: "team";
      readonly memberCount: number;
      readonly name: string;
    }
  | {
      readonly entityId: number;
      readonly id: number;
      readonly kind: "unit";
      readonly name: string;
      readonly teamCount: number;
    };

interface EditPanelCopyKeys {
  readonly deleteInfo: string;
  readonly kind: string;
  readonly title: string;
}

const EDIT_PANEL_COPY_KEYS: Record<
  OrganizationEditTarget["kind"],
  EditPanelCopyKeys
> = {
  entity: {
    deleteInfo: "OrganizationSettings.DeleteEntityConfirmInfo",
    kind: "OrganizationSettings.Entity",
    title: "OrganizationSettings.EntitySettings",
  },
  team: {
    deleteInfo: "OrganizationSettings.DeleteConfirmInfo",
    kind: "OrganizationSettings.Team",
    title: "OrganizationSettings.TeamSettings",
  },
  unit: {
    deleteInfo: "OrganizationSettings.DeleteConfirmInfo",
    kind: "OrganizationSettings.Unit",
    title: "OrganizationSettings.UnitSettings",
  },
};

export interface OrganizationEditOperation {
  readonly name: string;
  readonly target: OrganizationEditTarget;
}

interface EditPanelProps {
  readonly target: OrganizationEditTarget | null;
  readonly onClose: () => void;
  readonly onDelete: (
    target: OrganizationEditTarget,
  ) => Promise<OrganizationResult<void>>;
  readonly onSave: (
    operation: OrganizationEditOperation,
  ) => Promise<OrganizationResult<void>>;
}

type DeleteDialogState =
  | { readonly status: "closed" }
  | { readonly status: "entity" }
  | { readonly status: "team"; readonly memberCount: number }
  | { readonly status: "unit" };

export const OrganizationEditPanel = (
  props: EditPanelProps,
): JSX.Element | null => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [deleteDialogState, setDeleteDialogState] = useState<DeleteDialogState>(
    { status: "closed" },
  );
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setName(props.target?.name ?? "");
    setHasError(false);
    setDeleteDialogState({ status: "closed" });
  }, [props.target]);

  if (!props.target) {
    return null;
  }
  const target = props.target;

  const copyKeys = EDIT_PANEL_COPY_KEYS[target.kind];
  const kindLabel = t(copyKeys.kind);
  const panelTitle = t(copyKeys.title);
  const unitTeamCount = target.kind === "unit" ? target.teamCount : 0;

  const handleSave = async (): Promise<void> => {
    if (!name.trim() || name.trim() === target.name) return;
    setIsSaving(true);
    const result = await props.onSave({ name: name.trim(), target });
    setIsSaving(false);
    if (result.ok) {
      props.onClose();
      return;
    }
    setHasError(true);
  };

  const handleDelete = async (): Promise<void> => {
    setIsSaving(true);
    const result = await props.onDelete(target);
    setIsSaving(false);
    setDeleteDialogState({ status: "closed" });
    if (result.ok) {
      props.onClose();
      return;
    }
    setHasError(true);
  };

  const handleOpenDelete = (): void => {
    if (target.kind === "entity") {
      setDeleteDialogState({ status: "entity" });
      return;
    }
    if (target.kind === "unit") {
      setDeleteDialogState({ status: "unit" });
      return;
    }
    setDeleteDialogState({
      status: "team",
      memberCount: target.memberCount,
    });
  };

  const isDeleteDialogOpen = deleteDialogState.status !== "closed";

  const deleteDialogTitle =
    deleteDialogState.status === "unit"
      ? t("OrganizationSettings.RemoveUnitConfirmTitle")
      : deleteDialogState.status === "team"
        ? t("OrganizationSettings.RemoveTeamConfirmTitle")
        : t("OrganizationSettings.DeleteConfirmTitle");
  const deleteDialogAction =
    deleteDialogState.status === "unit"
      ? t("OrganizationSettings.RemoveUnitConfirmAction")
      : deleteDialogState.status === "team"
        ? t("OrganizationSettings.Remove", { kind: kindLabel })
        : t("OrganizationSettings.Delete");
  const deleteDialogInfo =
    deleteDialogState.status === "unit"
      ? t(
          unitTeamCount > 0
            ? "OrganizationSettings.RemoveUnitWithTeamsConfirmInfo"
            : "OrganizationSettings.RemoveEmptyUnitConfirmInfo",
          { count: unitTeamCount },
        )
      : deleteDialogState.status === "team"
        ? deleteDialogState.memberCount === 0
          ? t("OrganizationSettings.RemoveEmptyTeamConfirmInfo")
          : t("OrganizationSettings.RemoveTeamWithMembersConfirmInfo", {
              count: deleteDialogState.memberCount,
            })
        : deleteDialogState.status === "entity"
          ? t(copyKeys.deleteInfo)
          : t("OrganizationSettings.DeleteConfirmInfo");

  const handleClose = (): void => {
    if (!isSaving) props.onClose();
  };

  return (
    <>
      <SidePanel
        isOpen
        closeButtonMode="icon-only"
        onClose={handleClose}
        title={panelTitle}
        titleId="organization-edit-panel-title"
      >
        <PanelSection>
          <PanelActionRow>
            <PanelInputContent>
              <InputField
                size="compact"
                fullWidth
                label={t("OrganizationSettings.NameLabel", {
                  kind: kindLabel,
                })}
                value={name}
                onChange={setName}
                maxLength={100}
              />
            </PanelInputContent>
            <PrimaryButton
              size="small"
              label={t("OrganizationSettings.SaveChanges")}
              disabled={!name.trim() || name.trim() === target.name}
              isLoading={isSaving}
              onClick={() => void handleSave()}
            />
          </PanelActionRow>
        </PanelSection>
        <PanelSection>
          <PanelActionRow>
            <PanelActionContent>
              <PanelSectionTitle>
                {t("OrganizationSettings.RemoveTitle", { kind: kindLabel })}
              </PanelSectionTitle>
              <DangerCopy>{t("OrganizationSettings.RemoveInfo")}</DangerCopy>
            </PanelActionContent>
            <PrimaryButton
              size="small"
              iconName="v2/trash"
              label={t("OrganizationSettings.Remove", { kind: kindLabel })}
              disabled={isSaving}
              styleOverrides={{
                backgroundColor: ColorV2.NegativeRed,
                hoverBackgroundColor: ColorV2.NegativeRed,
              }}
              onClick={handleOpenDelete}
            />
          </PanelActionRow>
        </PanelSection>
        {hasError && (
          <ErrorBox>{t("OrganizationSettings.RequestFailed")}</ErrorBox>
        )}
      </SidePanel>
      <Modal
        label={deleteDialogTitle}
        size="compact"
        variant="confirm"
        iconName="v2/trash"
        centerText
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          if (!isSaving) setDeleteDialogState({ status: "closed" });
        }}
        footer={
          <DialogActions>
            <SecondaryButton
              size="small"
              label={t("OrganizationSettings.Cancel")}
              disabled={isSaving}
              onClick={() => setDeleteDialogState({ status: "closed" })}
            />
            <PrimaryButton
              size="small"
              label={deleteDialogAction}
              isLoading={isSaving}
              styleOverrides={{
                backgroundColor: ColorV2.NegativeRed,
                hoverBackgroundColor: ColorV2.NegativeRed,
              }}
              onClick={() => void handleDelete()}
            />
          </DialogActions>
        }
      >
        <DialogCopy>{deleteDialogInfo}</DialogCopy>
      </Modal>
    </>
  );
};

interface MembersPanelProps {
  readonly teamId: number | null;
  readonly assignWorker: (
    params: MemberMutationParams,
  ) => Promise<OrganizationResult<void>>;
  readonly loadMembers: (
    teamId: number,
  ) => Promise<OrganizationResult<OrganizationPerson[]>>;
  readonly loadWorkers: () => Promise<OrganizationResult<OrganizationPerson[]>>;
  readonly onClose: () => void;
  readonly removeWorker: (
    params: MemberMutationParams,
  ) => Promise<OrganizationResult<void>>;
}

interface MemberMutationParams {
  readonly teamId: number;
  readonly workerId: number;
}

export const OrganizationMembersPanel = (
  props: MembersPanelProps,
): JSX.Element => {
  const { t } = useTranslation();
  const teamId = props.teamId;
  const loadMembers = props.loadMembers;
  const loadWorkers = props.loadWorkers;
  const [companyState] = useCompanyStore();
  const [members, setMembers] = useState<OrganizationPerson[]>([]);
  const [workers, setWorkers] = useState<OrganizationPerson[]>([]);
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [loadedTeamId, setLoadedTeamId] = useState<number | null>(null);
  const [memberToRemove, setMemberToRemove] =
    useState<OrganizationPerson | null>(null);
  const activeTeamIdRef = useRef<number | null>(teamId);
  const refreshRequestIdRef = useRef(0);

  const refresh = useCallback(
    async (id: number): Promise<void> => {
      const requestId = refreshRequestIdRef.current + 1;
      refreshRequestIdRef.current = requestId;
      setIsLoading(true);
      setHasError(false);
      setHasLoadError(false);
      const [memberResult, workerResult] = await Promise.all([
        loadMembers(id),
        loadWorkers(),
      ]);
      if (
        activeTeamIdRef.current !== id ||
        refreshRequestIdRef.current !== requestId
      ) {
        return;
      }
      if (memberResult.ok && workerResult.ok) {
        setMembers(memberResult.value);
        setWorkers(workerResult.value);
      } else {
        setHasLoadError(true);
      }
      setLoadedTeamId(id);
      setIsLoading(false);
    },
    [loadMembers, loadWorkers],
  );

  useEffect(() => {
    activeTeamIdRef.current = teamId;
    setLoadedTeamId(null);
    setMembers([]);
    setWorkers([]);
    setQuery("");
    setMemberToRemove(null);
    setHasLoadError(false);
    if (teamId == null) {
      setIsLoading(false);
    } else {
      setIsLoading(true);
      void refresh(teamId);
    }

    return () => {
      if (activeTeamIdRef.current === teamId) {
        activeTeamIdRef.current = null;
      }
      refreshRequestIdRef.current += 1;
    };
  }, [refresh, teamId]);

  const memberIds = useMemo(
    () => new Set(members.map((worker) => worker.workerId)),
    [members],
  );
  const availableWorkers = useMemo(() => {
    return filterWorkers({
      query,
      workers: workers.filter((worker) => !memberIds.has(worker.workerId)),
    });
  }, [memberIds, query, workers]);
  const workerToRemove =
    memberToRemove == null
      ? null
      : (workers.find(
          (worker) => worker.workerId === memberToRemove.workerId,
        ) ?? memberToRemove);
  const isRemovingLastTeamMembership =
    teamId != null &&
    workerToRemove != null &&
    !hasOtherTeamMembership({ currentTeamId: teamId, worker: workerToRemove });

  const avatarUrl = (worker: OrganizationPerson): string | undefined => {
    if (!worker.externalId) return undefined;
    const image = companyState.companyWorkerImages?.[worker.externalId];
    return image && image !== "no-image" ? image : undefined;
  };

  const mutateMember = async (
    operation: () => Promise<OrganizationResult<void>>,
  ): Promise<void> => {
    if (teamId == null) return;
    const mutationTeamId = teamId;
    setIsLoading(true);
    const result = await operation();
    if (activeTeamIdRef.current !== mutationTeamId) return;
    await refresh(mutationTeamId);
    if (!result.ok) {
      setHasError(true);
    }
  };

  const addMember = (workerId: number): void => {
    if (teamId == null) return;
    void mutateMember(() => props.assignWorker({ teamId, workerId }));
  };

  const confirmRemoveMember = async (): Promise<void> => {
    if (teamId == null || memberToRemove == null) return;
    const workerId = memberToRemove.workerId;
    await mutateMember(() => props.removeWorker({ teamId, workerId }));
    if (activeTeamIdRef.current !== teamId) return;
    setMemberToRemove((current) =>
      current?.workerId === workerId ? null : current,
    );
  };

  const isTeamLoading = teamId != null && loadedTeamId !== teamId;

  return (
    <>
      <SidePanel
        isOpen={teamId != null}
        closeButtonMode="icon-only"
        onClose={props.onClose}
        title={t("OrganizationSettings.ManageMembers")}
        titleId="organization-members-panel-title"
      >
        {isTeamLoading ? (
          <PanelLoading role="status">
            <PanelSpinner aria-hidden="true" />
            <LoadingText>
              {t("OrganizationSettings.LoadingMembers")}
            </LoadingText>
          </PanelLoading>
        ) : hasLoadError ? (
          <PanelLoading role="alert">
            <ErrorBox>{t("OrganizationSettings.MembersLoadFailed")}</ErrorBox>
            <SecondaryButton
              size="small"
              label={t("OrganizationSettings.Retry")}
              onClick={() => {
                if (teamId != null) {
                  setLoadedTeamId(null);
                  void refresh(teamId);
                }
              }}
            />
          </PanelLoading>
        ) : (
          <>
            <PanelSection>
              <PanelSectionTitle>
                {t("OrganizationSettings.TeamMembersCount", {
                  count: members.length,
                })}
              </PanelSectionTitle>
              {members.length === 0 ? (
                <DialogCopy>
                  {t("OrganizationSettings.NoTeamMembers")}
                </DialogCopy>
              ) : (
                <PanelRows>
                  {members.map((worker) => {
                    const isInactive = isWorkerInactive(worker);
                    const identity = (
                      <MemberIdentity>
                        {isInactive ? (
                          <TooltipHover
                            text={t("OrganizationSettings.InactiveWorker")}
                            fullWidth
                          >
                            <AvatarChip
                              title={getWorkerName(worker)}
                              subTitle={getWorkerRole(worker)}
                              avatarUrl={avatarUrl(worker)}
                            />
                          </TooltipHover>
                        ) : (
                          <AvatarChip
                            title={getWorkerName(worker)}
                            subTitle={getWorkerRole(worker)}
                            avatarUrl={avatarUrl(worker)}
                          />
                        )}
                      </MemberIdentity>
                    );

                    return (
                      <MemberRow key={worker.workerId} $isInactive={isInactive}>
                        {identity}
                        <TooltipHover
                          text={t("OrganizationSettings.RemoveMember", {
                            name: getWorkerName(worker),
                          })}
                        >
                          <IconButton
                            type="button"
                            disabled={isLoading}
                            aria-label={t("OrganizationSettings.RemoveMember", {
                              name: getWorkerName(worker),
                            })}
                            onClick={() => setMemberToRemove(worker)}
                          >
                            <CustomIcon
                              name="v2/trash"
                              size="20px"
                              color={ColorV2.NegativeRed}
                            />
                          </IconButton>
                        </TooltipHover>
                      </MemberRow>
                    );
                  })}
                </PanelRows>
              )}
            </PanelSection>
            <PanelSection>
              <PanelSectionTitle>
                {t("OrganizationSettings.AddMembers")}
              </PanelSectionTitle>
              <InputField
                size="compact"
                ariaLabel={t("OrganizationSettings.SearchPeople")}
                value={query}
                onChange={setQuery}
                onClear={() => setQuery("")}
                placeholder={t("OrganizationSettings.SearchPeople")}
                iconName="v2/search"
                iconPosition="left"
                fullWidth
              />
              {availableWorkers.length === 0 && !isLoading ? (
                <DialogCopy>
                  {query
                    ? t("OrganizationSettings.NoPeopleMatch")
                    : t("OrganizationSettings.NoAvailableMembers")}
                </DialogCopy>
              ) : (
                <PanelRows>
                  {availableWorkers.map((worker) => {
                    const isInactive = isWorkerInactive(worker);
                    const identity = (
                      <MemberIdentity>
                        {isInactive ? (
                          <TooltipHover
                            text={t("OrganizationSettings.InactiveWorker")}
                            fullWidth
                          >
                            <AvatarChip
                              title={getWorkerName(worker)}
                              subTitle={getWorkerRole(worker)}
                              avatarUrl={avatarUrl(worker)}
                            />
                          </TooltipHover>
                        ) : (
                          <AvatarChip
                            title={getWorkerName(worker)}
                            subTitle={getWorkerRole(worker)}
                            avatarUrl={avatarUrl(worker)}
                          />
                        )}
                      </MemberIdentity>
                    );

                    return (
                      <MemberRow key={worker.workerId} $isInactive={isInactive}>
                        {identity}
                        <SecondaryButton
                          size="small"
                          iconName="v2/plus"
                          label={t("OrganizationSettings.AddToTeam")}
                          disabled={isLoading}
                          onClick={() => addMember(worker.workerId)}
                        />
                      </MemberRow>
                    );
                  })}
                </PanelRows>
              )}
            </PanelSection>
            {hasError && (
              <ErrorBox>{t("OrganizationSettings.RequestFailed")}</ErrorBox>
            )}
          </>
        )}
      </SidePanel>
      <Modal
        label={t("OrganizationSettings.RemoveMemberConfirmTitle")}
        size="compact"
        variant="confirm"
        titleSize="small"
        centerText
        isOpen={memberToRemove != null}
        onClose={() => {
          if (!isLoading) setMemberToRemove(null);
        }}
        footer={
          <DialogActions>
            <SecondaryButton
              size="small"
              label={t("OrganizationSettings.Cancel")}
              disabled={isLoading}
              onClick={() => setMemberToRemove(null)}
            />
            <PrimaryButton
              size="small"
              label={t("OrganizationSettings.RemoveMemberConfirmAction")}
              isLoading={isLoading}
              styleOverrides={{
                backgroundColor: ColorV2.NegativeRed,
                hoverBackgroundColor: ColorV2.NegativeRed,
              }}
              onClick={() => void confirmRemoveMember()}
            />
          </DialogActions>
        }
      >
        <DialogCopy>
          {t(
            isRemovingLastTeamMembership
              ? "OrganizationSettings.RemoveLastMemberConfirmInfo"
              : "OrganizationSettings.RemoveMemberConfirmInfo",
            {
              name: workerToRemove ? getWorkerName(workerToRemove) : "",
            },
          )}
        </DialogCopy>
      </Modal>
    </>
  );
};
